import { motion, useAnimationFrame } from 'motion/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import Character3D, { type CharacterConfig } from '../game/elements/Character3D';
import { PROPS_CATALOG } from '@/types/props';

// Configuration for background characters
interface BackgroundCharacter {
  id: string;
  x: number;
  y: number;
  size: number;
  config: Partial<CharacterConfig>;
  props: string[];
  behavior: 'wander' | 'patrol' | 'idle' | 'explore';
  speed: number;
  direction: 'up' | 'down' | 'left' | 'right' | null;
  targetX?: number;
  targetY?: number;
  behaviorTimer: number;
  layer: number; // For depth layering
}

interface AnimatedBackgroundLayerProps {
  theme?: 'home' | 'game' | 'shop' | 'levels';
  performance?: 'high' | 'medium' | 'low';
  characterCount?: number;
  className?: string;
}

// Character variations with different personalities
const CHARACTER_VARIANTS: Array<{
  config: Partial<CharacterConfig>;
  props: string[];
  behavior: BackgroundCharacter['behavior'];
  speed: number;
}> = [
  {
    config: {
      sizes: { 
        characterSize: 0.3, 
        cubeSize: 0.9,
        shadow: { width: 0.8, height: 0.2, offsetY: 0.1 }
      },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 50%, #e53e3e 100%)',
          back: 'linear-gradient(135deg, #e53e3e 0%, #c53030 50%, #9c2222 100%)',
          top: 'linear-gradient(135deg, #fc8181 0%, #f56565 50%, #e53e3e 100%)',
          bottom: 'linear-gradient(135deg, #c53030 0%, #9c2222 50%, #742a2a 100%)',
          left: 'linear-gradient(135deg, #f56565 0%, #e53e3e 50%, #d53f41 100%)',
          right: 'linear-gradient(135deg, #f76565 0%, #e63e3e 50%, #d63f41 100%)',
        },
        effects: {
          portal: 'radial-gradient(circle, rgba(255,107,107,0.3) 0%, transparent 70%)',
          bubble: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
          shadow: 'radial-gradient(ellipse, rgba(0,0,0,0.3) 0%, transparent 70%)'
        },
        borders: {
          face: '2px solid rgba(0, 0, 0, 0.2)',
          bubble: '1px solid rgba(100, 200, 255, 0.3)'
        }
      },
      face: { 
        eyes: { 
          size: '25%',
          containerWidth: '60%',
          containerMarginTop: '10%',
          gap: '25%',
          color: '#000',
          shine: {
            size: '40%',
            position: { top: '20%', left: '25%' },
            color: 'rgba(255, 255, 255, 0.8)'
          }
        },
        mouth: {
          width: '35%',
          heights: {
            neutral: '12%',
            happy: '20%',
            shocked: '25%'
          },
          color: 'rgba(0, 0, 0, 0.3)'
        }
      },
    },
    props: ['tiny_hat', 'small_backpack'],
    behavior: 'wander',
    speed: 0.5,
  },
  {
    config: {
      sizes: { 
        characterSize: 0.4, 
        cubeSize: 0.85,
        shadow: { width: 0.8, height: 0.2, offsetY: 0.1 }
      },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #4ecdc4 0%, #45b7b8 50%, #3d9e9f 100%)',
          back: 'linear-gradient(135deg, #3d9e9f 0%, #358688 50%, #2d6d70 100%)',
          top: 'linear-gradient(135deg, #68d8d6 0%, #4ecdc4 50%, #45b7b8 100%)',
          bottom: 'linear-gradient(135deg, #358688 0%, #2d6d70 50%, #245559 100%)',
          left: 'linear-gradient(135deg, #45b7b8 0%, #3da8aa 50%, #35989b 100%)',
          right: 'linear-gradient(135deg, #47bab8 0%, #3faaa8 50%, #379a98 100%)',
        },
        effects: {
          portal: 'radial-gradient(circle, rgba(78,205,196,0.3) 0%, transparent 70%)',
          bubble: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
          shadow: 'radial-gradient(ellipse, rgba(0,0,0,0.3) 0%, transparent 70%)'
        },
        borders: {
          face: '2px solid rgba(0, 0, 0, 0.2)',
          bubble: '1px solid rgba(100, 200, 255, 0.3)'
        }
      },
      face: { 
        eyes: { 
          size: '35%', 
          gap: '20%',
          containerWidth: '60%',
          containerMarginTop: '10%',
          color: '#000',
          shine: {
            size: '40%',
            position: { top: '20%', left: '25%' },
            color: 'rgba(255, 255, 255, 0.8)'
          }
        },
        mouth: {
          width: '35%',
          heights: {
            neutral: '12%',
            happy: '20%',
            shocked: '25%'
          },
          color: 'rgba(0, 0, 0, 0.3)'
        }
      },
    },
    props: ['explorer_compass'],
    behavior: 'explore',
    speed: 0.8,
  },
  {
    config: {
      sizes: { 
        characterSize: 0.6, 
        cubeSize: 0.8,
        shadow: { width: 0.8, height: 0.2, offsetY: 0.1 }
      },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #a55eea 0%, #9742d6 50%, #8e44ad 100%)',
          back: 'linear-gradient(135deg, #8e44ad 0%, #7d3c98 50%, #6c3483 100%)',
          top: 'linear-gradient(135deg, #be7dff 0%, #a55eea 50%, #9742d6 100%)',
          bottom: 'linear-gradient(135deg, #7d3c98 0%, #6c3483 50%, #5b2c6f 100%)',
          left: 'linear-gradient(135deg, #9742d6 0%, #8a3ec4 50%, #7d39b1 100%)',
          right: 'linear-gradient(135deg, #9945d8 0%, #8c40c6 50%, #7f3bb3 100%)',
        },
        effects: {
          portal: 'radial-gradient(circle, rgba(165,94,234,0.3) 0%, transparent 70%)',
          bubble: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
          shadow: 'radial-gradient(ellipse, rgba(0,0,0,0.3) 0%, transparent 70%)'
        },
        borders: {
          face: '2px solid rgba(0, 0, 0, 0.2)',
          bubble: '1px solid rgba(100, 200, 255, 0.3)'
        }
      },
      face: { 
        eyes: { 
          size: '20%', 
          gap: '30%',
          containerWidth: '60%',
          containerMarginTop: '10%',
          color: '#000',
          shine: {
            size: '40%',
            position: { top: '20%', left: '25%' },
            color: 'rgba(255, 255, 255, 0.8)'
          }
        }, 
        mouth: { 
          width: '25%',
          heights: {
            neutral: '12%',
            happy: '20%',
            shocked: '25%'
          },
          color: 'rgba(0, 0, 0, 0.3)'
        }
      },
    },
    props: ['meditation_crystal'],
    behavior: 'idle',
    speed: 0.2,
  },
  {
    config: {
      sizes: { 
        characterSize: 0.35, 
        cubeSize: 0.95,
        shadow: { width: 0.8, height: 0.2, offsetY: 0.1 }
      },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #26de81 0%, #20bf6b 50%, #16a085 100%)',
          back: 'linear-gradient(135deg, #16a085 0%, #138d75 50%, #117a65 100%)',
          top: 'linear-gradient(135deg, #2ed573 0%, #26de81 50%, #20bf6b 100%)',
          bottom: 'linear-gradient(135deg, #138d75 0%, #117a65 50%, #0f6954 100%)',
          left: 'linear-gradient(135deg, #20bf6b 0%, #1bb55c 50%, #16aa4c 100%)',
          right: 'linear-gradient(135deg, #22c26d 0%, #1db75e 50%, #18ac4e 100%)',
        },
        effects: {
          portal: 'radial-gradient(circle, rgba(38,222,129,0.3) 0%, transparent 70%)',
          bubble: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
          shadow: 'radial-gradient(ellipse, rgba(0,0,0,0.3) 0%, transparent 70%)'
        },
        borders: {
          face: '2px solid rgba(0, 0, 0, 0.2)',
          bubble: '1px solid rgba(100, 200, 255, 0.3)'
        }
      },
      face: { 
        eyes: { 
          size: '28%',
          containerWidth: '60%',
          containerMarginTop: '10%',
          gap: '25%',
          color: '#000',
          shine: {
            size: '40%',
            position: { top: '20%', left: '25%' },
            color: 'rgba(255, 255, 255, 0.8)'
          }
        },
        mouth: {
          width: '35%',
          heights: {
            neutral: '12%',
            happy: '20%',
            shocked: '25%'
          },
          color: 'rgba(0, 0, 0, 0.3)'
        }
      },
    },
    props: ['speed_boost'],
    behavior: 'patrol',
    speed: 1.2,
  },
  {
    config: {
      sizes: { 
        characterSize: 0.45, 
        cubeSize: 0.88,
        shadow: { width: 0.8, height: 0.2, offsetY: 0.1 }
      },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #fed330 0%, #f39c12 50%, #e67e22 100%)',
          back: 'linear-gradient(135deg, #e67e22 0%, #d35400 50%, #c0392b 100%)',
          top: 'linear-gradient(135deg, #fffa65 0%, #fed330 50%, #f39c12 100%)',
          bottom: 'linear-gradient(135deg, #d35400 0%, #c0392b 50%, #a93226 100%)',
          left: 'linear-gradient(135deg, #f39c12 0%, #ea8f0a 50%, #e18302 100%)',
          right: 'linear-gradient(135deg, #f4a214 0%, #eb9110 50%, #e28508 100%)',
        },
        effects: {
          portal: 'radial-gradient(circle, rgba(254,211,48,0.3) 0%, transparent 70%)',
          bubble: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
          shadow: 'radial-gradient(ellipse, rgba(0,0,0,0.3) 0%, transparent 70%)'
        },
        borders: {
          face: '2px solid rgba(0, 0, 0, 0.2)',
          bubble: '1px solid rgba(100, 200, 255, 0.3)'
        }
      },
      face: { 
        eyes: { 
          size: '32%', 
          gap: '15%',
          containerWidth: '60%',
          containerMarginTop: '10%',
          color: '#000',
          shine: {
            size: '40%',
            position: { top: '20%', left: '25%' },
            color: 'rgba(255, 255, 255, 0.8)'
          }
        },
        mouth: {
          width: '35%',
          heights: {
            neutral: '12%',
            happy: '20%',
            shocked: '25%'
          },
          color: 'rgba(0, 0, 0, 0.3)'
        }
      },
    },
    props: ['star_collector'],
    behavior: 'wander',
    speed: 0.7,
  },
];

const AnimatedBackgroundLayer: React.FC<AnimatedBackgroundLayerProps> = ({
  theme = 'home',
  performance = 'medium',
  characterCount,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastUpdateTime = useRef<number>(0);
  const [containerSize, setContainerSize] = useState({ width: 1000, height: 1000 });

  // Determine character count based on performance and theme
  const actualCharacterCount = useMemo(() => {
    if (characterCount) return characterCount;
    
    const baseCounts = {
      high: { home: 12, game: 8, shop: 10, levels: 15 },
      medium: { home: 8, game: 5, shop: 6, levels: 10 },
      low: { home: 4, game: 2, shop: 3, levels: 5 },
    };
    
    return baseCounts[performance][theme];
  }, [performance, theme, characterCount]);

  // Initialize background characters
  const [characters, setCharacters] = useState<BackgroundCharacter[]>(() => {
    return Array.from({ length: actualCharacterCount }, (_, i) => {
      const variant = CHARACTER_VARIANTS[i % CHARACTER_VARIANTS.length];
      return {
        id: `bg-char-${i}`,
        x: Math.random() * containerSize.width,
        y: Math.random() * containerSize.height,
        size: 30 + Math.random() * 40, // 30-70px
        config: variant.config,
        props: variant.props,
        behavior: variant.behavior,
        speed: variant.speed * (0.5 + Math.random() * 0.5), // Randomize speed slightly
        direction: null,
        behaviorTimer: Math.random() * 5000, // Random initial behavior timer
        layer: Math.floor(Math.random() * 3), // 3 depth layers
      };
    });
  });

  // Convert prop IDs to actual prop objects
  const getCharacterProps = useCallback((propIds: string[]) => {
    return propIds
      .map(propId => PROPS_CATALOG.find(prop => prop.id === propId))
      .filter((prop): prop is NonNullable<typeof prop> => prop !== undefined);
  }, []);

  // Update character positions and behaviors
  const updateCharacters = useCallback((deltaTime: number) => {
    setCharacters(prevCharacters => {
      return prevCharacters.map(char => {
        const newChar = { ...char };
        newChar.behaviorTimer -= deltaTime;

        // Handle behavior changes
        if (newChar.behaviorTimer <= 0) {
          switch (newChar.behavior) {
            case 'wander':
              // Pick a random direction
              const directions: (typeof newChar.direction)[] = ['up', 'down', 'left', 'right', null];
              newChar.direction = directions[Math.floor(Math.random() * directions.length)];
              newChar.behaviorTimer = 1000 + Math.random() * 3000; // 1-4 seconds
              break;
              
            case 'patrol':
              // Move in straight lines, change direction at edges
              if (!newChar.direction) {
                newChar.direction = ['up', 'down', 'left', 'right'][Math.floor(Math.random() * 4)] as any;
              }
              newChar.behaviorTimer = 2000 + Math.random() * 4000; // 2-6 seconds
              break;
              
            case 'explore':
              // Move towards random targets
              if (!newChar.targetX || !newChar.targetY) {
                newChar.targetX = Math.random() * containerSize.width;
                newChar.targetY = Math.random() * containerSize.height;
              }
              
              const dx = newChar.targetX - newChar.x;
              const dy = newChar.targetY - newChar.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              
              if (distance < 20) {
                // Reached target, pick new one
                newChar.targetX = Math.random() * containerSize.width;
                newChar.targetY = Math.random() * containerSize.height;
                newChar.direction = null;
              } else {
                // Move towards target
                if (Math.abs(dx) > Math.abs(dy)) {
                  newChar.direction = dx > 0 ? 'right' : 'left';
                } else {
                  newChar.direction = dy > 0 ? 'down' : 'up';
                }
              }
              newChar.behaviorTimer = 500; // Check frequently for exploration
              break;
              
            case 'idle':
              // Mostly stay still, occasional small movements
              newChar.direction = Math.random() < 0.3 ? ['up', 'down', 'left', 'right'][Math.floor(Math.random() * 4)] as any : null;
              newChar.behaviorTimer = 3000 + Math.random() * 7000; // 3-10 seconds
              break;
          }
        }

        // Update position based on direction
        if (newChar.direction) {
          const moveSpeed = newChar.speed * deltaTime * 0.01; // Scale movement
          
          switch (newChar.direction) {
            case 'up':
              newChar.y = Math.max(0, newChar.y - moveSpeed);
              break;
            case 'down':
              newChar.y = Math.min(containerSize.height - newChar.size, newChar.y + moveSpeed);
              break;
            case 'left':
              newChar.x = Math.max(0, newChar.x - moveSpeed);
              break;
            case 'right':
              newChar.x = Math.min(containerSize.width - newChar.size, newChar.x + moveSpeed);
              break;
          }
        }

        // Wrap around edges for some behaviors
        if (newChar.behavior === 'wander') {
          if (newChar.x < -newChar.size) newChar.x = containerSize.width;
          if (newChar.x > containerSize.width) newChar.x = -newChar.size;
          if (newChar.y < -newChar.size) newChar.y = containerSize.height;
          if (newChar.y > containerSize.height) newChar.y = -newChar.size;
        }

        return newChar;
      });
    });
  }, [containerSize]);

  // Animation frame loop
  useAnimationFrame((time) => {
    if (performance === 'low') {
      // Only update every 3rd frame for low performance
      if (time % 3 !== 0) return;
    }
    
    const deltaTime = time - lastUpdateTime.current;
    lastUpdateTime.current = time;
    
    if (deltaTime > 0 && deltaTime < 100) { // Prevent huge jumps
      updateCharacters(deltaTime);
    }
  });

  // Update container size on mount/resize
  const updateContainerSize = useCallback(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setContainerSize({ width, height });
    }
  }, []);

  // Layer blur and opacity based on depth
  const getLayerStyle = (layer: number) => {
    const layerStyles = [
      { blur: 0, opacity: 0.3, scale: 1 },      // Front layer
      { blur: 1, opacity: 0.2, scale: 0.8 },   // Middle layer  
      { blur: 2, opacity: 0.15, scale: 0.6 },  // Back layer
    ];
    
    const style = layerStyles[layer] || layerStyles[0];
    return {
      filter: `blur(${style.blur}px)`,
      opacity: style.opacity,
      transform: `scale(${style.scale})`,
    };
  };

  return (
    <div 
      ref={containerRef}
      className={`animated-background-layer ${className}`}
      onLoad={updateContainerSize}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {characters.map((char) => (
        <motion.div
          key={char.id}
          style={{
            position: 'absolute',
            left: char.x,
            top: char.y,
            width: char.size,
            height: char.size,
            ...getLayerStyle(char.layer),
          }}
          animate={{
            x: char.x,
            y: char.y,
          }}
          transition={{
            type: 'spring',
            stiffness: 50,
            damping: 20,
            mass: 0.5,
          }}
        >
          <Character3D
            position={[0, 0]}
            direction={char.direction}
            isMoving={char.direction !== null}
            cellSize={char.size}
            gameState="playing"
            config={char.config}
            equippedProps={getCharacterProps(char.props)}
          />
        </motion.div>
      ))}
    </div>
  );
};

export default AnimatedBackgroundLayer;